import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { authApi } from '../services/api';
import { useAuthStore } from '../services/auth';

export default function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [companyRegNumber, setCompanyRegNumber] = useState('');
  const [vatNumber, setVatNumber] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [physicalAddress, setPhysicalAddress] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [sameAsPhysical, setSameAsPhysical] = useState(false);
  const [showCompanyDetails, setShowCompanyDetails] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { setAuth } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!companyName.trim()) {
      setError('Company name is required');
      return;
    }

    setIsLoading(true);

    try {
      const response = await authApi.register({
        email,
        password,
        company_name: companyName,
        company_registration_number: companyRegNumber || undefined,
        vat_number: vatNumber || undefined,
        billing_email: billingEmail || undefined,
        phone: phone || undefined,
        physical_address: physicalAddress || undefined,
        billing_address: sameAsPhysical ? physicalAddress : (billingAddress || undefined),
      });
      setAuth(response);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 py-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="HumanVu" className="h-16 w-auto mx-auto" />
          <span className="block mt-2 text-2xl font-bold text-gray-900 dark:text-white">HumanVu</span>
          <p className="mt-1 text-gray-500 dark:text-slate-400">Start your free 30-day trial</p>
        </div>

        {/* Trial Benefits */}
        <div className="mb-6 p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl">
          <p className="text-sm font-semibold text-primary-800 dark:text-primary-300 mb-2">
            Free trial includes:
          </p>
          <ul className="space-y-1.5">
            {['30 days full access', 'Up to 2 cameras/devices', 'No credit card required', 'All analytics features'].map((item) => (
              <li key={item} className="flex items-center gap-2 text-sm text-primary-700 dark:text-primary-400">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <Card>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            )}

            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />

            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
              />
              <button
                type="button"
                className="absolute right-3 top-8 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>

            <Input
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
            />

            <div className="border-t border-gray-200 dark:border-slate-700 pt-4">
              <Input
                label="Company Name"
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Your company name"
                required
              />
            </div>

            <button
              type="button"
              className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300"
              onClick={() => setShowCompanyDetails(!showCompanyDetails)}
            >
              {showCompanyDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Billing details (optional)
            </button>

            {showCompanyDetails && (
              <div className="space-y-3 pl-2 border-l-2 border-gray-200 dark:border-slate-700">
                <Input
                  label="Company Registration Number"
                  type="text"
                  value={companyRegNumber}
                  onChange={(e) => setCompanyRegNumber(e.target.value)}
                  placeholder="e.g. 2024/123456/07"
                />
                <Input
                  label="VAT Number"
                  type="text"
                  value={vatNumber}
                  onChange={(e) => setVatNumber(e.target.value)}
                  placeholder="e.g. 4123456789"
                />
                <Input
                  label="Billing Email"
                  type="email"
                  value={billingEmail}
                  onChange={(e) => setBillingEmail(e.target.value)}
                  placeholder="accounts@company.co.za"
                />
                <Input
                  label="Phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+27 12 345 6789"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Physical Address
                  </label>
                  <textarea
                    value={physicalAddress}
                    onChange={(e) => setPhysicalAddress(e.target.value)}
                    placeholder="Street address, city, province, postal code"
                    rows={2}
                    className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400">
                  <input
                    type="checkbox"
                    checked={sameAsPhysical}
                    onChange={(e) => setSameAsPhysical(e.target.checked)}
                    className="rounded border-gray-300 dark:border-slate-600"
                  />
                  Billing address same as physical
                </label>
                {!sameAsPhysical && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                      Billing Address
                    </label>
                    <textarea
                      value={billingAddress}
                      onChange={(e) => setBillingAddress(e.target.value)}
                      placeholder="Billing address if different"
                      rows={2}
                      className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}
              </div>
            )}

            <Button type="submit" className="w-full" isLoading={isLoading}>
              Start Free Trial
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500 dark:text-slate-400">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-500 dark:text-primary-400 hover:text-primary-700">
              Sign in
            </Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
